import { Button, DrawerClose, DrawerFooter, Separator } from "@monbits/ui";
import { useQuery } from "@tanstack/react-query";
import { RotateCw, TriangleAlert } from "lucide-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import CopyButton from "src/components/copy-button/copy-button";
import DetailRow from "src/components/detail-row/detail-row";
import { EntityDrawer } from "src/components/entity-drawer";
import { StatusBadge } from "src/components/status-badge";
import paymentProvider from "src/providers/payment-provider";
import { formatCryptoAmount } from "src/utils/format";
import { getStatusBadgeProps } from "src/utils/status-config";
import { formatDateTime, formatFiat, formatUsd } from "./format";

function StatusValue({ status }: { status: string }) {
	const config = getStatusBadgeProps(status);
	return (
		<StatusBadge type={config.type} icon={config.icon}>
			{config.label}
		</StatusBadge>
	);
}

export function PaymentDetail() {
	const { paymentId } = useParams<{ paymentId: string }>();

	const {
		data: payment,
		isLoading,
		isError,
		refetch,
	} = useQuery({
		queryKey: ["adminPayment", paymentId],
		queryFn: () => {
			if (!paymentId) {
				throw new Error("Payment ID is required");
			}
			return paymentProvider.getPaymentByPublicId(paymentId);
		},
		enabled: Boolean(paymentId),
	});

	const isNotFound = !isLoading && !isError && !payment;

	return (
		<EntityDrawer
			title="Payment Details"
			isLoading={isLoading}
			isNotFound={isNotFound}
			notFoundMessage="Payment not found"
			parentPath="/payments"
		>
			{isError ? (
				<div className="flex flex-col items-center justify-center h-full p-6 text-center">
					<TriangleAlert className="h-12 w-12 text-destructive mb-4" />
					<h3 className="text-lg font-semibold mb-2">Couldn't load payment</h3>
					<p className="text-sm text-muted-foreground mb-6">
						Something went wrong while fetching this payment.
					</p>
					<Button variant="outline" onClick={() => refetch()}>
						<RotateCw className="h-4 w-4" />
						Retry
					</Button>
				</div>
			) : (
				payment && (
					<div className="p-6">
						<div className="space-y-6">
							<div>
								<h3 className="text-sm font-semibold mb-3">
									Basic Information
								</h3>
								<div className="space-y-2 text-sm">
									<DetailRow
										label="Status"
										value={<StatusValue status={payment.status} />}
									/>
									<DetailRow
										label="Type"
										value={
											<span className="capitalize font-medium">
												{payment.type}
											</span>
										}
									/>
									<DetailRow
										label="Invoiced"
										value={
											<span className="font-mono font-medium tabular-nums">
												{formatFiat(payment.price, payment.currency)}
											</span>
										}
									/>
									<DetailRow
										label="Test Mode"
										value={payment.isTest ? "Yes" : "No"}
									/>
								</div>
							</div>

							<Separator />

							<div>
								<h3 className="text-sm font-semibold mb-3">Identifiers</h3>
								<div className="space-y-2 text-sm">
									<DetailRow
										label="Payment ID"
										value={
											<>
												<span className="font-mono font-medium">
													{payment.id}
												</span>
												<CopyButton
													value={String(payment.id)}
													label="Payment ID"
												/>
											</>
										}
									/>
									<DetailRow
										label="Public ID"
										value={
											<>
												<span className="font-mono text-xs break-all font-medium">
													{payment.publicId}
												</span>
												<CopyButton
													value={payment.publicId}
													label="Payment public ID"
												/>
											</>
										}
									/>
									<DetailRow
										label="Merchant Order"
										value={
											<>
												<span>{payment.merchantOrderId}</span>
												<CopyButton
													value={payment.merchantOrderId}
													label="Merchant order ID"
												/>
											</>
										}
									/>
								</div>
							</div>

							<Separator />

							<div>
								<h3 className="text-sm font-semibold mb-3">
									Links &amp; Timing
								</h3>
								<div className="space-y-2 text-sm">
									{payment.redirectUrl && (
										<div className="flex items-start justify-between gap-2">
											<div className="flex flex-col gap-1">
												<span className="text-muted-foreground">
													Redirect URL
												</span>
												<span className="font-mono text-xs break-all font-medium">
													{payment.redirectUrl}
												</span>
											</div>
											<CopyButton
												value={payment.redirectUrl}
												label="Redirect URL"
											/>
										</div>
									)}
									{payment.description && (
										<DetailRow
											label="Description"
											value={payment.description}
										/>
									)}
									<DetailRow
										label="Created"
										value={formatDateTime(payment.createdAt)}
									/>
									<DetailRow
										label="Updated"
										value={formatDateTime(payment.updatedAt)}
									/>
									{payment.expiresAt && (
										<DetailRow
											label="Expires"
											value={formatDateTime(payment.expiresAt)}
										/>
									)}
									{payment.webhookSentAt && (
										<DetailRow
											label="Webhook Sent"
											value={formatDateTime(payment.webhookSentAt)}
										/>
									)}
								</div>
							</div>

							<Separator />

							<div>
								<h3 className="text-sm font-semibold mb-3">
									Merchant &amp; Customer
								</h3>
								<div className="space-y-2 text-sm">
									<DetailRow label="Merchant" value={payment.merchantName} />
									{payment.merchantUserName && (
										<DetailRow
											label="User Name"
											value={payment.merchantUserName}
										/>
									)}
									{payment.merchantUserEmail && (
										<DetailRow
											label="User Email"
											value={payment.merchantUserEmail}
										/>
									)}
									{payment.customerEmail && (
										<DetailRow
											label="Customer Email"
											value={payment.customerEmail}
										/>
									)}
								</div>
							</div>

							<Separator />

							<div>
								<h3 className="text-sm font-semibold mb-3">
									Latest Transaction
								</h3>
								{payment.latestTransaction ? (
									<div className="space-y-2 text-sm">
										<DetailRow
											label="Transaction ID"
											value={
												<span className="font-mono font-medium">
													{payment.latestTransaction.id}
												</span>
											}
										/>
										<DetailRow
											label="Status"
											value={
												<StatusValue
													status={payment.latestTransaction.status}
												/>
											}
										/>
										<DetailRow
											label="Type"
											value={
												<span className="capitalize font-medium">
													{payment.latestTransaction.type}
												</span>
											}
										/>
										{payment.latestTransaction.currency && (
											<DetailRow
												label="Currency"
												value={
													<span className="font-medium">
														{payment.latestTransaction.currency}
													</span>
												}
											/>
										)}
										{payment.latestTransaction.amount &&
											payment.latestTransaction.currency && (
												<DetailRow
													label="Expected"
													value={
														<span className="font-mono font-medium tabular-nums">
															{formatCryptoAmount(
																payment.latestTransaction.amount,
																{ ticker: payment.latestTransaction.currency },
															)}{" "}
															{payment.latestTransaction.currency}
														</span>
													}
												/>
											)}
										{payment.latestTransaction.factAmount &&
											payment.latestTransaction.currency && (
												<DetailRow
													label="Received"
													value={
														<span className="font-mono font-medium tabular-nums">
															{formatCryptoAmount(
																payment.latestTransaction.factAmount,
																{ ticker: payment.latestTransaction.currency },
															)}{" "}
															{payment.latestTransaction.currency}
														</span>
													}
												/>
											)}
										{formatUsd(payment.latestTransaction.usdAmount) && (
											<DetailRow
												label="USD value"
												value={
													<span className="font-mono font-medium tabular-nums">
														≈ {formatUsd(payment.latestTransaction.usdAmount)}
													</span>
												}
											/>
										)}
										{payment.latestTransaction.transactionHash && (
											<div className="flex items-start justify-between gap-2">
												<div className="flex flex-col gap-1">
													<span className="text-muted-foreground">Hash</span>
													<span className="font-mono text-xs break-all font-medium">
														{payment.latestTransaction.transactionHash}
													</span>
												</div>
												<CopyButton
													value={payment.latestTransaction.transactionHash}
													label="Transaction hash"
												/>
											</div>
										)}
									</div>
								) : (
									<p className="text-sm text-muted-foreground">
										No transaction has been linked yet.
									</p>
								)}
							</div>
						</div>

						<DrawerFooter className="px-0 pt-6">
							<DrawerClose asChild>
								<Button variant="outline" className="w-full" size="lg">
									Close
								</Button>
							</DrawerClose>
						</DrawerFooter>
					</div>
				)
			)}
		</EntityDrawer>
	);
}
